import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { champsPourType, TypeAnalyseSpeciale } from '@/lib/analysesSpeciales';

interface ResultatSpecifiqueInputProps {
  type: TypeAnalyseSpeciale;
  valeur: string;
  donnees: Record<string, string>;
  fichierUrl: string | null;
  fichierNom: string | null;
  unite: string;
  onValeurChange: (v: string) => void;
  onDonneesChange: (d: Record<string, string>) => void;
  onFichierChange: (url: string | null, nom: string | null) => void;
}

export default function ResultatSpecifiqueInput({
  type,
  valeur,
  donnees,
  fichierUrl,
  fichierNom,
  unite,
  onValeurChange,
  onDonneesChange,
  onFichierChange,
}: ResultatSpecifiqueInputProps) {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  const handleDonneesFieldChange = (key: string, value: string) => {
    onDonneesChange({ ...donnees, [key]: value });
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setUploadError('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const fd = new FormData();
      fd.append('file', file);
      fd.append('prefix', 'resultat_analyse');

      const res = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/upload-resultat-analyse`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${session?.access_token}` },
          body: fd,
        },
      );

      const json = await res.json().catch(() => null);
      if (!res.ok || !json?.success) {
        throw new Error(json?.error || "Échec de l'envoi du fichier");
      }

      onFichierChange(json.url, json.fileName || file.name);
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : "Erreur lors de l'envoi du fichier");
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const retirerFichier = () => {
    onFichierChange(null, null);
    setUploadError('');
  };

  // Saisie structurée NFS / ATB / ECBU
  if (type === 'nfs' || type === 'atb' || type === 'ecbu') {
    const champs = champsPourType(type);
    return (
      <div className="space-y-3">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
          {champs.map((champ) => (
            <div key={champ.key} className="flex flex-col">
              <label className="text-[11px] font-semibold text-gray-600 mb-1">{champ.label}</label>
              <input
                type={champ.type}
                value={donnees[champ.key] || ''}
                onChange={(e) => handleDonneesFieldChange(champ.key, e.target.value)}
                onClick={(e) => e.stopPropagation()}
                placeholder={champ.label}
                className="px-2.5 py-2 border border-gray-300 rounded-lg text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all"
              />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Fichier uniquement (Électrophorèse Hb, Électrophorèse des protéines)
  if (type === 'fichier') {
    return (
      <div className="space-y-2">
        {fichierUrl ? (
          <div className="flex items-center justify-between gap-2 bg-teal-50 border border-teal-200 rounded-lg px-3 py-2">
            <span className="text-sm text-teal-800 flex items-center gap-2 min-w-0">
              <i className="ri-file-pdf-2-line text-teal-600 text-lg flex-shrink-0"></i>
              <span className="truncate">{fichierNom || 'Fichier joint'}</span>
            </span>
            <button
              onClick={(e) => { e.stopPropagation(); retirerFichier(); }}
              className="text-teal-500 hover:text-red-500 cursor-pointer transition-colors flex-shrink-0"
              title="Retirer le fichier"
            >
              <i className="ri-close-circle-line text-lg"></i>
            </button>
          </div>
        ) : (
          <label
            onClick={(e) => e.stopPropagation()}
            className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-teal-500 hover:text-teal-600 cursor-pointer transition-colors"
          >
            {uploading ? (
              <>
                <i className="ri-loader-4-line animate-spin"></i>Envoi en cours...
              </>
            ) : (
              <>
                <i className="ri-upload-cloud-2-line text-lg"></i>
                Joindre le résultat (PDF ou image)
              </>
            )}
            <input
              type="file"
              accept="application/pdf,image/jpeg,image/png,image/webp,image/gif"
              onChange={handleFileSelect}
              className="hidden"
              disabled={uploading}
            />
          </label>
        )}
        {uploadError && (
          <p className="text-xs text-red-600 flex items-center gap-1">
            <i className="ri-error-warning-line"></i>{uploadError}
          </p>
        )}
      </div>
    );
  }

  // Fichier + valeur (HbA1c)
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={valeur}
          onChange={(e) => { e.stopPropagation(); onValeurChange(e.target.value); }}
          onClick={(e) => e.stopPropagation()}
          placeholder={`Valeur${unite ? ` (${unite})` : ''}`}
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all"
        />
        {unite && <span className="text-xs text-gray-400 whitespace-nowrap">{unite}</span>}
      </div>

      {fichierUrl ? (
        <div className="flex items-center justify-between gap-2 bg-teal-50 border border-teal-200 rounded-lg px-3 py-2">
          <span className="text-sm text-teal-800 flex items-center gap-2 min-w-0">
            <i className="ri-file-pdf-2-line text-teal-600 text-lg flex-shrink-0"></i>
            <span className="truncate">{fichierNom || 'Fichier joint'}</span>
          </span>
          <button
            onClick={(e) => { e.stopPropagation(); retirerFichier(); }}
            className="text-teal-500 hover:text-red-500 cursor-pointer transition-colors flex-shrink-0"
            title="Retirer le fichier"
          >
            <i className="ri-close-circle-line text-lg"></i>
          </button>
        </div>
      ) : (
        <label
          onClick={(e) => e.stopPropagation()}
          className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-teal-500 hover:text-teal-600 cursor-pointer transition-colors"
        >
          {uploading ? (
            <>
              <i className="ri-loader-4-line animate-spin"></i>Envoi en cours...
            </>
          ) : (
            <>
              <i className="ri-upload-cloud-2-line text-lg"></i>
              Joindre le résultat (PDF ou image)
            </>
          )}
          <input
            type="file"
            accept="application/pdf,image/jpeg,image/png,image/webp,image/gif"
            onChange={handleFileSelect}
            className="hidden"
            disabled={uploading}
          />
        </label>
      )}
      {uploadError && (
        <p className="text-xs text-red-600 flex items-center gap-1">
          <i className="ri-error-warning-line"></i>{uploadError}
        </p>
      )}
    </div>
  );
}